// src/store/index.js
import axiosInstance from "@/axiosInstance";
import { createStore } from "vuex";

const store = createStore({
  state: {
    currentUser: null,
    clients: [],
    guests: [],
    operators: [],
    items: [],
    orders: [],
    ordersPagination: {},
    quotes: [],
    pagination: { next: null, previous: null },
  },
  getters: {
    // ---- Ruoli/permessi (D10A) ----
    // Ruolo effettivo dell'utente loggato (fallback su isAdmin per retro-compat).
    userRole: (state) => {
      const u = state.currentUser;
      if (!u) return null;
      const ROLES = ["dev", "admin", "manager", "infermiere", "operator"];
      if (ROLES.includes(u.role)) return u.role;
      return u.isAdmin ? "admin" : "operator";
    },
    // dev/admin: accesso amministrativo (dati società, stats economiche).
    isAdminUser: (_state, getters) =>
      ["dev", "admin"].includes(getters.userRole),
    // dev/admin/manager: gestione gestionale/amministrativa (anagrafiche,
    // economia per-ospite, gestione presidi, archiviazione documenti).
    // NB: NON include infermiere (ruolo sanitario/operativo).
    canManage: (_state, getters) =>
      ["dev", "admin", "manager"].includes(getters.userRole),
    // dev/admin/manager: alias semantico per la gestione.
    isManagerUser: (_state, getters) =>
      ["dev", "admin", "manager"].includes(getters.userRole),
    // Area sanitaria/assistenziale: dev/admin/manager/infermiere (no operator).
    isCareUser: (_state, getters) =>
      ["dev", "admin", "manager", "infermiere"].includes(getters.userRole),
    canAccessCareArea: (_state, getters) => getters.isCareUser,
    // Presidi/Forniture: consultazione (lettura) — include infermiere.
    canViewSupplies: (_state, getters) =>
      ["dev", "admin", "manager", "infermiere"].includes(getters.userRole),
    // Presidi/Forniture: gestione stock (carico DDT, movimenti) — no infermiere.
    canManageSupplies: (_state, getters) =>
      ["dev", "admin", "manager"].includes(getters.userRole),
    // Archiviazione PDF DDT presidi — gestione (no infermiere/operator).
    canArchiveDdtPdf: (_state, getters) =>
      ["dev", "admin", "manager"].includes(getters.userRole),
    // Funzioni economiche per-ospite (es. generazione retta) — no infermiere.
    canManageEconomic: (_state, getters) =>
      ["dev", "admin", "manager"].includes(getters.userRole),
    // Solo dev/admin vedono le statistiche economiche.
    canViewEconomics: (_state, getters) => getters.isAdminUser,
    // Solo dev/admin vedono/modificano i dati società.
    canViewCompany: (_state, getters) => getters.isAdminUser,
    // operator: per ora limitato al pannello mobile/placeholder.
    isOperatorRole: (_state, getters) => getters.userRole === "operator",

    "items/list/filtered": (state) => (category) =>
      (state.items || []).filter((item) => item.category === category) ?? [],
    "items/list": (state) => state.items ?? [],
    "items/pagination": (state) => state.pagination,
    "clients/list": (state) => state.clients ?? [],
    "clients/pagination": (state) => state.pagination,
    "guests/list": (state) => state.guests ?? [],
    "guests/pagination": (state) => state.pagination,
    "operators/list": (state) => state.operators ?? [],
    "quotes/list": (state) => state.quotes ?? [],
    "quotes/pagination": (state) => state.pagination,
    "orders/list": (state) => state.orders ?? [],
    "orders/pagination": (state) => state.ordersPagination,
  },
  mutations: {
    SET_CURRENT_USER(state, user) {
      state.currentUser = user;
    },
    CLEAR_CURRENT_USER(state) {
      state.currentUser = null;
    },

    SET_CLIENTS(state, clients) {
      state.clients = clients;
    },
    // Nuova mutation per aggiungere al volo un cliente
    ADD_CLIENT(state, newClient) {
      state.clients.push(newClient);
    },
    // Nuova mutation per rimuovere un cliente
    REMOVE_CLIENT(state, clientId) {
      state.clients = state.clients.filter((client) => client.id !== clientId);
    },
    // Nuova mutaion per aggiornare un cliente esistente
    UPDATE_CLIENT(state, updatedClient) {
      const index = state.clients.findIndex(
        (client) => client.id === updatedClient.id
      );
      if (index !== -1) {
        // Aggiorna i campi in modo reattivo
        state.clients.splice(index, 1, updatedClient);
      }
    },

    // ---------------------------------------------------------------------------//

    SET_GUESTS(state, guests) {
      state.guests = guests;
    },
    ADD_GUEST(state, newGuest) {
      state.guests.push(newGuest);
    },
    REMOVE_GUEST(state, guestId) {
      state.guests = state.guests.filter((guest) => guest.id !== guestId);
    },
    UPDATE_GUEST(state, updatedGuest) {
      const index = state.guests.findIndex(
        (guest) => guest.id === updatedGuest.id
      );
      if (index !== -1) {
        state.guests.splice(index, 1, updatedGuest);
      }
    },

    // ---------------------------------------------------------------------------//

    SET_OPERATORS(state, operators) {
      state.operators = operators;
    },
    // Nuova mutation per aggiungere al volo un operatore
    ADD_OPERATOR(state, newOperator) {
      state.operators.push(newOperator);
    },
    // Nuova mutation per rimuovere un operatore
    REMOVE_OPERATOR(state, operatorId) {
      state.operators = state.operators.filter((op) => op.id !== operatorId);
    },
    // Nuova mutaion per aggiornare un operatore esistente
    UPDATE_OPERATOR(state, updatedOperator) {
      const index = state.operators.findIndex(
        (op) => op.id === updatedOperator.id
      );
      if (index !== -1) {
        // Aggiorna i campi in modo reattivo
        state.operators.splice(index, 1, updatedOperator);
      }
    },

    // ---------------------------------------------------------------------------//

    SET_ITEMS(state, items) {
      state.items = items;
    },
    // Nuova mutation per la gestione della paginazione della lista degli articoli
    // SET_PAGINATION(state, pageInfo) {
    //   state.pagination = pageInfo;
    // },

    SET_PAGINATION(state, pageInfo) {
      state.pagination = {
        currentPage: pageInfo.currentPage,
        totalPages: pageInfo.totalPages,
        totalItems: pageInfo.totalItems,
        itemsPerPage: pageInfo.itemsPerPage,
        startItem: (pageInfo.currentPage - 1) * pageInfo.itemsPerPage + 1,
        endItem: Math.min(
          pageInfo.currentPage * pageInfo.itemsPerPage,
          pageInfo.totalItems
        ),
      };
    },
    // Nuova mutation per aggiungere al volo un item
    ADD_ITEM(state, newItem) {
      state.items.push(newItem);
    },
    // Nuova mutation per rimuovere un item
    REMOVE_ITEM(state, itemId) {
      state.items = state.items.filter((item) => item.id !== itemId);
    },
    // Nuova mutaion per aggiornare un item esistente
    UPDATE_ITEM(state, updatedItem) {
      const index = state.items.findIndex((item) => item.id === updatedItem.id);
      if (index !== -1) {
        // Aggiorna i campi in modo reattivo
        state.items.splice(index, 1, updatedItem);
      }
    },

    // ---------------------------------------------------------------------------//

    SET_ORDERS(state, orders) {
      state.orders = orders;
    },
    SET_ORDERS_PAGINATION(state, pageInfo) {
      state.ordersPagination = {
        currentPage: pageInfo.currentPage,
        totalPages: pageInfo.totalPages,
        totalItems: pageInfo.totalItems,
        itemsPerPage: pageInfo.itemsPerPage,
        startItem:
          pageInfo.startItem ??
          (pageInfo.currentPage - 1) * pageInfo.itemsPerPage + 1,
        endItem:
          pageInfo.endItem ??
          Math.min(
            pageInfo.currentPage * pageInfo.itemsPerPage,
            pageInfo.totalItems
          ),
      };
    },

    // ---------------------------------------------------------------------------//

    SET_QUOTES(state, quotes) {
      state.quotes = quotes;
    },
    // Nuova mutation per aggiungere al volo un quote
    ADD_QUOTE(state, newQuote) {
      state.quotes.push(newQuote);
    },
    // Nuova mutation per rimuovere un quote
    REMOVE_QUOTE(state, quoteId) {
      state.quotes = state.quotes.filter((quote) => quote.id !== quoteId);
    },
    // Nuova mutaion per aggiornare un quote esistente
    UPDATE_QUOTE(state, updatedQuote) {
      const index = state.quotes.findIndex(
        (quote) => quote.id === updatedQuote.id
      );
      if (index !== -1) {
        // Aggiorna i campi in modo reattivo
        state.quotes.splice(index, 1, updatedQuote);
      }
    },
    // Per aggiungere documenti al bucket
    ADD_DOC_QUOTE(state, { quoteId, document }) {
      const index = state.quotes.findIndex((q) => q.id === quoteId);
      if (index === -1) return;

      // se non c’è ancora l’array, lo creo
      if (!Array.isArray(state.quotes[index].documents)) {
        // Vue 3: ok aggiungere una nuova proprietà reattiva
        state.quotes[index].documents = [];
      }

      state.quotes[index].documents.push(document);
    },
  },

  actions: {
    async fetchClients({ commit }, { page = 1, limit, search = "" } = {}) {
      try {
        const { data } = await axiosInstance.get("/client/get-all", {
          params: { page, limit, ...(search ? { search } : {}) },
        });
        commit("SET_CLIENTS", Array.isArray(data.results) ? data.results : []);
        commit("SET_PAGINATION", {
          currentPage: data.pagination.currentPage,
          totalPages: data.pagination.totalPages,
          totalItems: data.pagination.totalItems,
          itemsPerPage: data.pagination.itemsPerPage,
        });
        return data.results;
      } catch (err) {
        if (err.response?.status === 404) {
          // il server dice “nessun cliente”: trasformalo in array vuoto
          commit("SET_CLIENTS", []);
          // azzera la paginazione per coerenza UI
          commit("SET_PAGINATION", {
            currentPage: 1,
            totalPages: 1,
            totalItems: 0,
            itemsPerPage: 8,
          });
          return [];
        }
        console.error("fetchClients failed:", err);
        throw err; // lascia passare altri errori (500, 401…)
      }
    },

    // Nuova azione per creare un cliente
    async createClient({ dispatch }, payload) {
      try {
        const response = await axiosInstance.post("/client/add", payload);

        // commit("ADD_CLIENT", response.data.operator);
        await dispatch("fetchClients");
        return response;
      } catch (error) {
        console.error("Errore durante la creazione di un nuovo client:", error);
        throw error;
      }
    },

    // Nuova azione per aggiornare un cliente esistente
    async updateClient({ commit }, { clientId, payload }) {
      const response = await axiosInstance.patch(
        `client/update/${clientId}`,
        payload
      );
      commit("UPDATE_CLIENT", response.data.client);
      return response;
    },

    // Aggiungiamo un’azione per archiviare (o “eliminare”) il cliente
    async archiveClient({ commit }, clientId) {
      try {
        // Faccio la chiamata al backend
        const response = await axiosInstance.post(`client/archive/${clientId}`);
        // Se va a buon fine, aggiorno localmente lo store
        commit("REMOVE_CLIENT", clientId);
        return response;
      } catch (error) {
        console.error("Errore durante l'archiviazione del cliente: ", error);
        throw error;
      }
    },

    // ----------------------------- OSPITI (Guests) -----------------------------//

    async fetchGuests({ commit }, { page = 1, limit = 8, search = "" } = {}) {
      try {
        const { data } = await axiosInstance.get("/guests", {
          params: { page, limit, ...(search ? { search } : {}) },
        });
        commit("SET_GUESTS", Array.isArray(data.results) ? data.results : []);
        commit("SET_PAGINATION", {
          currentPage: data.pagination.currentPage,
          totalPages: data.pagination.totalPages,
          totalItems: data.pagination.totalItems,
          itemsPerPage: data.pagination.itemsPerPage,
        });
        return data.results;
      } catch (err) {
        if (err.response?.status === 404) {
          // nessun ospite: lista vuota + paginazione azzerata
          commit("SET_GUESTS", []);
          commit("SET_PAGINATION", {
            currentPage: 1,
            totalPages: 1,
            totalItems: 0,
            itemsPerPage: limit,
          });
          return [];
        }
        console.error("fetchGuests failed:", err);
        throw err;
      }
    },

    async fetchGuestById(_, guestId) {
      const { data } = await axiosInstance.get(`/guests/${guestId}`);
      return data.guest;
    },

    async createGuest({ dispatch }, payload) {
      try {
        const response = await axiosInstance.post("/guests", payload);
        await dispatch("fetchGuests");
        return response;
      } catch (error) {
        console.error("Errore durante la creazione di un nuovo ospite:", error);
        throw error;
      }
    },

    async updateGuest({ commit }, { guestId, payload }) {
      const response = await axiosInstance.put(`/guests/${guestId}`, payload);
      commit("UPDATE_GUEST", response.data.guest);
      return response;
    },

    async archiveGuest({ commit }, guestId) {
      try {
        const response = await axiosInstance.delete(`/guests/${guestId}`);
        commit("REMOVE_GUEST", guestId);
        return response;
      } catch (error) {
        console.error("Errore durante l'archiviazione dell'ospite:", error);
        throw error;
      }
    },

    // ------------------- DOCUMENTI OSPITE (Cartella documentale, D2) -----------//
    // Wrapper API semplici: il componente GuestDocumentsPanel tiene la lista in
    // stato LOCALE e ricarica dopo upload/archive.
    async fetchGuestDocuments(_, guestId) {
      const { data } = await axiosInstance.get(`/guests/${guestId}/documents`);
      return Array.isArray(data.results) ? data.results : [];
    },
    async uploadGuestDocument(_, { guestId, formData }) {
      // axios v1 imposta da solo Content-Type multipart con boundary per FormData.
      const { data } = await axiosInstance.post(
        `/guests/${guestId}/documents`,
        formData
      );
      return data.document;
    },
    async downloadGuestDocument(_, { guestId, documentId }) {
      // Ritorna l'intera response: il componente usa response.data (Blob).
      return axiosInstance.get(
        `/guests/${guestId}/documents/${documentId}/download`,
        { responseType: "blob" }
      );
    },
    async archiveGuestDocument(_, { guestId, documentId }) {
      return (
        await axiosInstance.delete(`/guests/${guestId}/documents/${documentId}`)
      ).data;
    },

    // ------------------- GENERAZIONE CONTRATTO OSPITE (D9) ---------------------//
    // Genera il contratto dal template DOCX attivo della struttura dell'ospite e
    // lo salva nella Cartella documentale. Ritorna il GuestDocument generato.
    async generateGuestContract(_, guestId) {
      const { data } = await axiosInstance.post(
        `/guests/${guestId}/contracts/generate`
      );
      return data.document;
    },

    // ------------------- DATI SOCIETÀ / EMITTENTE (D10A) -----------------------//
    // Lettura/scrittura riservate a dev/admin (il backend applica il 403).
    async fetchCompanySettings() {
      const { data } = await axiosInstance.get("/company-settings");
      return data.settings;
    },
    async updateCompanySettings(_, payload) {
      const { data } = await axiosInstance.patch("/company-settings", payload);
      return data.settings;
    },

    // ------------------- DOCUMENTI RETTA / BILLING (D10B) ----------------------//
    // Documento singolo ospite (dev/admin/manager). month/year/bankAccountId opzionali.
    async generateGuestBillingDocument(
      _,
      { guestId, month, year, bankAccountId }
    ) {
      const { data } = await axiosInstance.post(
        `/guests/${guestId}/billing/monthly-document`,
        { month, year, bankAccountId }
      );
      return data.document;
    },
    // Anteprima bulk struttura (dev/admin).
    async fetchFacilityBillingPreview(_, { facilityId, month, year }) {
      const { data } = await axiosInstance.get(
        `/facilities/${facilityId}/billing/monthly-documents/preview`,
        { params: { month, year } }
      );
      return data.preview;
    },
    // Generazione bulk struttura (dev/admin).
    async generateFacilityBillingBulk(
      _,
      { facilityId, month, year, skipExisting = true, bankAccountId }
    ) {
      const { data } = await axiosInstance.post(
        `/facilities/${facilityId}/billing/monthly-documents/bulk`,
        { month, year, skipExisting, bankAccountId }
      );
      return data.result;
    },

    // ------------------- CONTABILITÀ (D10B+) -----------------------------------//
    // Tutte riservate a dev/admin (il backend applica il 403). Archivio globale,
    // progressivi regolabili e note di credito.
    async fetchBillingDocuments(_, filters = {}) {
      const { data } = await axiosInstance.get("/billing/documents", {
        params: filters,
      });
      return Array.isArray(data.results) ? data.results : [];
    },
    async fetchBillingDocument(_, id) {
      const { data } = await axiosInstance.get(`/billing/documents/${id}`);
      return data;
    },
    async downloadBillingDocument(_, id) {
      return axiosInstance.get(`/billing/documents/${id}/download`, {
        responseType: "blob",
      });
    },
    async createBillingCreditNote(_, { id, amount, reason, bankAccountId }) {
      const { data } = await axiosInstance.post(
        `/billing/documents/${id}/credit-note`,
        { amount, reason, bankAccountId }
      );
      return data;
    },

    // ------------------- IMPOSTAZIONI: CONTI BANCARI (D10C) ---------------------//
    // Tutte riservate a dev/admin (il backend applica il 403).
    async fetchBankAccounts(_, { activeOnly = false } = {}) {
      const { data } = await axiosInstance.get("/settings/bank-accounts", {
        params: activeOnly ? { activeOnly: true } : {},
      });
      return Array.isArray(data.results) ? data.results : [];
    },
    async createBankAccount(_, payload) {
      const { data } = await axiosInstance.post(
        "/settings/bank-accounts",
        payload
      );
      return data.account;
    },
    async updateBankAccount(_, { id, payload }) {
      const { data } = await axiosInstance.patch(
        `/settings/bank-accounts/${id}`,
        payload
      );
      return data.account;
    },
    async setDefaultBankAccount(_, id) {
      const { data } = await axiosInstance.post(
        `/settings/bank-accounts/${id}/default`
      );
      return data.account;
    },
    async deactivateBankAccount(_, id) {
      const { data } = await axiosInstance.delete(
        `/settings/bank-accounts/${id}`
      );
      return data.account;
    },

    // ------------------- IMPOSTAZIONI: CATALOGHI CSV (D11A) ---------------------//
    async fetchCatalogs() {
      const { data } = await axiosInstance.get("/settings/catalogs");
      return Array.isArray(data.catalogs) ? data.catalogs : [];
    },
    async importAifaCatalog(_, formData) {
      const { data } = await axiosInstance.post(
        "/settings/catalogs/aifa/import",
        formData
      );
      return data;
    },
    async fetchBillingSequences() {
      const { data } = await axiosInstance.get("/billing/sequences");
      return Array.isArray(data.results) ? data.results : [];
    },
    async updateBillingSequence(_, { id, nextNumber, padding }) {
      const { data } = await axiosInstance.patch(`/billing/sequences/${id}`, {
        nextNumber,
        padding,
      });
      return data.sequence;
    },
    async createBillingSequence(_, payload) {
      const { data } = await axiosInstance.post("/billing/sequences", payload);
      return data.sequence;
    },

    // ------------------- PANNELLO OPERATORE (D12) ------------------------------//
    // Accessibile a tutti i ruoli autenticati. Nessun dato contabile.
    async fetchOperatorSummary(_, { facilityId, date } = {}) {
      const { data } = await axiosInstance.get("/operator-panel/summary", {
        params: { facilityId, ...(date ? { date } : {}) },
      });
      return data.summary;
    },
    async fetchOperatorGuestView(_, { guestId, facilityId, date } = {}) {
      const { data } = await axiosInstance.get(
        `/operator-panel/guests/${guestId}`,
        { params: { facilityId, ...(date ? { date } : {}) } }
      );
      return data.view;
    },
    async createCareActivity(_, { guestId, payload }) {
      const { data } = await axiosInstance.post(
        `/operator-panel/guests/${guestId}/actions`,
        payload
      );
      return data.activity;
    },
    // Review qualità assistenziale (D13) — vista aggregata read-only.
    // dev/admin/manager/infermiere (operator escluso lato API).
    async fetchCareReviewSummary(_, { facilityId, from, to, period } = {}) {
      const { data } = await axiosInstance.get("/care-review/summary", {
        params: {
          ...(facilityId ? { facilityId } : {}),
          ...(from ? { from } : {}),
          ...(to ? { to } : {}),
          ...(period ? { period } : {}),
        },
      });
      return data.summary;
    },
    // Diario assistenziale (dettaglio ospite, dev/admin/manager).
    async fetchGuestCareActivities(_, { guestId, from, to, type, status }) {
      const { data } = await axiosInstance.get(
        `/guests/${guestId}/care-activities`,
        {
          params: {
            ...(from ? { from } : {}),
            ...(to ? { to } : {}),
            ...(type ? { type } : {}),
            ...(status ? { status } : {}),
          },
        }
      );
      return Array.isArray(data.results) ? data.results : [];
    },

    // ------------------- MAGAZZINO FORNITURE OSPITE (D12B) ---------------------//
    // Gestione (forniture/carichi/bolle/overview) riservata a dev/admin/manager.
    async fetchGuestSupplies(_, guestId) {
      const { data } = await axiosInstance.get(`/guests/${guestId}/supplies`);
      return Array.isArray(data.results) ? data.results : [];
    },
    async createGuestSupply(_, { guestId, payload }) {
      const { data } = await axiosInstance.post(
        `/guests/${guestId}/supplies`,
        payload
      );
      return data.item;
    },
    async updateGuestSupply(_, { guestId, supplyItemId, payload }) {
      const { data } = await axiosInstance.patch(
        `/guests/${guestId}/supplies/${supplyItemId}`,
        payload
      );
      return data.item;
    },
    async deleteGuestSupply(_, { guestId, supplyItemId }) {
      const { data } = await axiosInstance.delete(
        `/guests/${guestId}/supplies/${supplyItemId}`
      );
      return data;
    },
    async createSupplyMovement(_, { guestId, supplyItemId, payload }) {
      const { data } = await axiosInstance.post(
        `/guests/${guestId}/supplies/${supplyItemId}/movements`,
        payload
      );
      return data;
    },
    async fetchSupplyMovements(_, { guestId, itemId }) {
      const { data } = await axiosInstance.get(
        `/guests/${guestId}/supplies/movements`,
        { params: itemId ? { itemId } : {} }
      );
      return Array.isArray(data.results) ? data.results : [];
    },
    async createSupplyDeliveryNote(_, { guestId, formData }) {
      const { data } = await axiosInstance.post(
        `/guests/${guestId}/supplies/delivery-notes`,
        formData
      );
      return data;
    },
    async fetchSupplyDeliveryNotes(_, guestId) {
      const { data } = await axiosInstance.get(
        `/guests/${guestId}/supplies/delivery-notes`
      );
      return Array.isArray(data.results) ? data.results : [];
    },
    async fetchSuppliesOverview(_, { facilityId, lowStockOnly } = {}) {
      const { data } = await axiosInstance.get("/supplies/overview", {
        params: {
          ...(facilityId ? { facilityId } : {}),
          ...(lowStockOnly ? { lowStockOnly: true } : {}),
        },
      });
      return Array.isArray(data.results) ? data.results : [];
    },
    // Annullamento (soft delete) di un movimento — solo dev/admin (il backend
    // applica il 403). Motivo obbligatorio.
    async voidSupplyMovement(_, { guestId, movementId, reason }) {
      const { data } = await axiosInstance.delete(
        `/guests/${guestId}/supplies/movements/${movementId}`,
        { data: { reason } }
      );
      return data;
    },
    // Vista aggregata bolle/documenti forniture.
    async fetchDeliveryNotesOverview(_, filters = {}) {
      const { data } = await axiosInstance.get("/supplies/delivery-notes", {
        params: filters,
      });
      return Array.isArray(data.results) ? data.results : [];
    },

    // ------------------- STRUTTURE / DASHBOARD (D6) ----------------------------//
    async fetchFacilities() {
      const { data } = await axiosInstance.get("/facilities");
      return Array.isArray(data.results) ? data.results : [];
    },
    async fetchFacility(_, facilityId) {
      const { data } = await axiosInstance.get(`/facilities/${facilityId}`);
      return data.facility;
    },
    async createFacility(_, payload) {
      return (await axiosInstance.post("/facilities", payload)).data.facility;
    },
    async updateFacility(_, { facilityId, payload }) {
      return (await axiosInstance.put(`/facilities/${facilityId}`, payload))
        .data.facility;
    },
    async archiveFacility(_, facilityId) {
      return (await axiosInstance.delete(`/facilities/${facilityId}`)).data;
    },
    async fetchDashboardSummary() {
      const { data } = await axiosInstance.get("/dashboard/summary");
      return data.summary;
    },
    // Viste facility-scoped (D6C): riusano gli endpoint scoped del backend.
    async fetchFacilityGuests(_, facilityId) {
      const { data } = await axiosInstance.get(
        `/facilities/${facilityId}/guests`
      );
      return Array.isArray(data.results) ? data.results : [];
    },
    async fetchFacilityRooms(_, facilityId) {
      const { data } = await axiosInstance.get(
        `/facilities/${facilityId}/rooms`
      );
      return Array.isArray(data.results) ? data.results : [];
    },
    async fetchFacilityOccupancy(_, facilityId) {
      return (
        await axiosInstance.get(`/facilities/${facilityId}/occupancy/overview`)
      ).data;
    },
    // Vista analitica struttura (D6E): rendimento per stanza, classifica, storico.
    async fetchFacilityOccupancyAnalytics(_, facilityId) {
      return (
        await axiosInstance.get(`/facilities/${facilityId}/occupancy/analytics`)
      ).data;
    },

    // ---- Template documentali struttura (D8) ----
    async fetchFacilityDocumentTemplates(_, facilityId) {
      const { data } = await axiosInstance.get(
        `/facilities/${facilityId}/document-templates`
      );
      return Array.isArray(data.results) ? data.results : [];
    },
    async uploadFacilityDocumentTemplate(_, { facilityId, formData }) {
      const { data } = await axiosInstance.post(
        `/facilities/${facilityId}/document-templates`,
        formData
      );
      return data.template;
    },
    async updateFacilityDocumentTemplate(
      _,
      { facilityId, templateId, payload }
    ) {
      const { data } = await axiosInstance.patch(
        `/facilities/${facilityId}/document-templates/${templateId}`,
        payload
      );
      return data.template;
    },
    async archiveFacilityDocumentTemplate(_, { facilityId, templateId }) {
      return (
        await axiosInstance.delete(
          `/facilities/${facilityId}/document-templates/${templateId}`
        )
      ).data;
    },
    async downloadFacilityDocumentTemplate(_, { facilityId, templateId }) {
      return axiosInstance.get(
        `/facilities/${facilityId}/document-templates/${templateId}/download`,
        { responseType: "blob" }
      );
    },

    // ------------------- FARMACI OSPITE / STOCK (D3B) --------------------------//
    // Wrapper API JSON: il componente GuestMedicationsPanel tiene la lista in
    // stato LOCALE e ricarica dopo create/update/archive.
    async fetchGuestMedications(_, guestId) {
      const { data } = await axiosInstance.get(
        `/guests/${guestId}/medications`
      );
      return Array.isArray(data.results) ? data.results : [];
    },
    async createGuestMedication(_, { guestId, payload }) {
      const { data } = await axiosInstance.post(
        `/guests/${guestId}/medications`,
        payload
      );
      return data.medication;
    },
    async updateGuestMedication(_, { guestId, medicationId, payload }) {
      const { data } = await axiosInstance.put(
        `/guests/${guestId}/medications/${medicationId}`,
        payload
      );
      return data.medication;
    },
    async archiveGuestMedication(_, { guestId, medicationId }) {
      return (
        await axiosInstance.delete(
          `/guests/${guestId}/medications/${medicationId}`
        )
      ).data;
    },

    // ------------------- CATALOGO FARMACI AIFA (D11) ---------------------------//
    // Ricerca server-side per autocomplete dei farmaci personali. SOLO supporto
    // al data-entry: la terapia continua a usare i farmaci personali salvati.
    async searchMedicationCatalog(_, { q, limit = 20 } = {}) {
      const query = (q || "").trim();
      if (query.length < 2) return [];
      const { data } = await axiosInstance.get("/medication-catalog/search", {
        params: { q: query, limit },
      });
      return Array.isArray(data.results) ? data.results : [];
    },

    // ----------- FARMACI PERSONALI + CARICHI STOCK (D3B-refactor) --------------//
    // Anagrafica farmaci (la disponibilità è calcolata dal backend, read-only).
    async fetchGuestMedicationItems(_, guestId) {
      const { data } = await axiosInstance.get(
        `/guests/${guestId}/medication-items`
      );
      return Array.isArray(data.results) ? data.results : [];
    },
    async fetchGuestMedicationItem(_, { guestId, itemId }) {
      const { data } = await axiosInstance.get(
        `/guests/${guestId}/medication-items/${itemId}`
      );
      return data.item;
    },
    async createGuestMedicationItem(_, { guestId, payload }) {
      const { data } = await axiosInstance.post(
        `/guests/${guestId}/medication-items`,
        payload
      );
      return data.item;
    },
    async updateGuestMedicationItem(_, { guestId, itemId, payload }) {
      const { data } = await axiosInstance.put(
        `/guests/${guestId}/medication-items/${itemId}`,
        payload
      );
      return data.item;
    },
    async archiveGuestMedicationItem(_, { guestId, itemId }) {
      return (
        await axiosInstance.delete(
          `/guests/${guestId}/medication-items/${itemId}`
        )
      ).data;
    },

    // Carichi stock di un farmaco.
    async fetchGuestMedicationStockLoads(_, { guestId, itemId }) {
      const { data } = await axiosInstance.get(
        `/guests/${guestId}/medication-items/${itemId}/stock-loads`
      );
      return Array.isArray(data.results) ? data.results : [];
    },
    async createGuestMedicationStockLoad(_, { guestId, itemId, payload }) {
      const { data } = await axiosInstance.post(
        `/guests/${guestId}/medication-items/${itemId}/stock-loads`,
        payload
      );
      return data.load;
    },
    async updateGuestMedicationStockLoad(
      _,
      { guestId, itemId, loadId, payload }
    ) {
      const { data } = await axiosInstance.put(
        `/guests/${guestId}/medication-items/${itemId}/stock-loads/${loadId}`,
        payload
      );
      return data.load;
    },
    async archiveGuestMedicationStockLoad(_, { guestId, itemId, loadId }) {
      return (
        await axiosInstance.delete(
          `/guests/${guestId}/medication-items/${itemId}/stock-loads/${loadId}`
        )
      ).data;
    },

    // --------- STOCK FARMACI: MOVIMENTI / OVERVIEW (D14) ----------------------//
    // Lettura: CARE_ROLES (incl. infermiere). Rettifica: MANAGEMENT. Void: ADMIN.
    async fetchMedicationStockOverview(_, guestId) {
      const { data } = await axiosInstance.get(
        `/guests/${guestId}/medication-stock/overview`
      );
      return Array.isArray(data.results) ? data.results : [];
    },
    async fetchMedicationStockMovements(_, { guestId, medicationItemId } = {}) {
      const { data } = await axiosInstance.get(
        `/guests/${guestId}/medication-stock/movements`,
        { params: medicationItemId ? { medicationItemId } : {} }
      );
      return Array.isArray(data.results) ? data.results : [];
    },
    async createMedicationStockAdjustment(_, { guestId, payload }) {
      const { data } = await axiosInstance.post(
        `/guests/${guestId}/medication-stock/adjustments`,
        payload
      );
      return data.movement;
    },
    async voidMedicationStockMovement(_, { guestId, movementId, reason }) {
      const { data } = await axiosInstance.delete(
        `/guests/${guestId}/medication-stock/movements/${movementId}`,
        { data: { reason } }
      );
      return data;
    },

    // --------- SCHEDE TERAPIA STRUTTURATE: PLAN + LINES (D4) --------------------//
    async fetchGuestTherapyPlans(_, guestId) {
      const { data } = await axiosInstance.get(
        `/guests/${guestId}/therapy-plans`
      );
      return Array.isArray(data.results) ? data.results : [];
    },
    async fetchGuestTherapyPlan(_, { guestId, planId }) {
      const { data } = await axiosInstance.get(
        `/guests/${guestId}/therapy-plans/${planId}`
      );
      return data.plan;
    },
    async createGuestTherapyPlan(_, { guestId, payload }) {
      const { data } = await axiosInstance.post(
        `/guests/${guestId}/therapy-plans`,
        payload
      );
      return data.plan;
    },
    async updateGuestTherapyPlan(_, { guestId, planId, payload }) {
      const { data } = await axiosInstance.put(
        `/guests/${guestId}/therapy-plans/${planId}`,
        payload
      );
      return data.plan;
    },
    async archiveGuestTherapyPlan(_, { guestId, planId }) {
      const { data } = await axiosInstance.post(
        `/guests/${guestId}/therapy-plans/${planId}/archive`
      );
      return data.plan;
    },
    async fetchGuestTherapyPlanLines(_, { guestId, planId }) {
      const { data } = await axiosInstance.get(
        `/guests/${guestId}/therapy-plans/${planId}/lines`
      );
      return Array.isArray(data.results) ? data.results : [];
    },
    async createGuestTherapyPlanLine(_, { guestId, planId, payload }) {
      const { data } = await axiosInstance.post(
        `/guests/${guestId}/therapy-plans/${planId}/lines`,
        payload
      );
      return data.line;
    },
    async updateGuestTherapyPlanLine(_, { guestId, planId, lineId, payload }) {
      const { data } = await axiosInstance.put(
        `/guests/${guestId}/therapy-plans/${planId}/lines/${lineId}`,
        payload
      );
      return data.line;
    },
    async archiveGuestTherapyPlanLine(_, { guestId, planId, lineId }) {
      return (
        await axiosInstance.delete(
          `/guests/${guestId}/therapy-plans/${planId}/lines/${lineId}`
        )
      ).data;
    },
    // Genera lo snapshot PDF della scheda e lo salva in Cartella documentale (D5).
    // Ritorna l'intero payload { message, document } per una lettura stabile.
    async generateGuestTherapyPlanPdf(_, { guestId, planId }) {
      const { data } = await axiosInstance.post(
        `/guests/${guestId}/therapy-plans/${planId}/generate-pdf`
      );
      return data;
    },

    // ------------------- TERAPIE OSPITE (D3) -----------------------------------//
    // Wrapper API: il componente GuestTherapiesPanel tiene la lista in stato
    // LOCALE e ricarica dopo create/update/archive.
    async fetchGuestTherapies(_, guestId) {
      const { data } = await axiosInstance.get(`/guests/${guestId}/therapies`);
      return Array.isArray(data.results) ? data.results : [];
    },
    async createGuestTherapy(_, { guestId, payload }) {
      const { data } = await axiosInstance.post(
        `/guests/${guestId}/therapies`,
        payload
      );
      return data.therapy;
    },
    async updateGuestTherapy(_, { guestId, therapyId, payload }) {
      const { data } = await axiosInstance.put(
        `/guests/${guestId}/therapies/${therapyId}`,
        payload
      );
      return data.therapy;
    },
    async archiveGuestTherapy(_, { guestId, therapyId }) {
      return (
        await axiosInstance.delete(`/guests/${guestId}/therapies/${therapyId}`)
      ).data;
    },

    // ------------------- CAMERE / POSTI LETTO / OCCUPAZIONE --------------------//
    // Approccio C2: queste action sono semplici wrapper API. Le pagine tengono
    // rooms/beds/occupancyOverview nello stato LOCALE e ricaricano (fetchRooms /
    // fetchOccupancyOverview) dopo create/update/delete/assign/release.

    async fetchRooms() {
      const { data } = await axiosInstance.get("/rooms");
      return Array.isArray(data.results) ? data.results : [];
    },
    async fetchRoomById(_, id) {
      const { data } = await axiosInstance.get(`/rooms/${id}`);
      return data.room;
    },
    async createRoom(_, payload) {
      return (await axiosInstance.post("/rooms", payload)).data;
    },
    async updateRoom(_, { id, payload }) {
      return (await axiosInstance.put(`/rooms/${id}`, payload)).data;
    },
    async deactivateRoom(_, id) {
      return (await axiosInstance.delete(`/rooms/${id}`)).data;
    },

    async fetchBeds(_, roomId) {
      const { data } = await axiosInstance.get("/beds", {
        params: roomId ? { roomId } : {},
      });
      return Array.isArray(data.results) ? data.results : [];
    },
    async createBed(_, payload) {
      return (await axiosInstance.post("/beds", payload)).data;
    },
    async updateBed(_, { id, payload }) {
      return (await axiosInstance.put(`/beds/${id}`, payload)).data;
    },
    async deactivateBed(_, id) {
      return (await axiosInstance.delete(`/beds/${id}`)).data;
    },

    async fetchOccupancyOverview() {
      return (await axiosInstance.get("/occupancy/overview")).data;
    },
    async assignGuestToBed(_, payload) {
      return (await axiosInstance.post("/occupancy/assign", payload)).data;
    },
    // Nome primario coerente col backend/occupancy
    async releaseAssignment(_, payload) {
      return (await axiosInstance.post("/occupancy/release", payload)).data;
    },
    // Alias retro-compatibile
    async releaseBed({ dispatch }, payload) {
      return dispatch("releaseAssignment", payload);
    },

    // ---------------------------------------------------------------------------//

    async fetchOperators({ commit }) {
      try {
        const response = await axiosInstance.get("/auth/get-all"); // endpoint della tua API
        commit("SET_OPERATORS", response.data);
      } catch (error) {
        console.error("Errore durante il fetch degli operators:", error);
        throw error;
      }
    },

    // Nuova azione per creare un operatore
    async createOperator({ dispatch }, payload) {
      try {
        const response = await axiosInstance.post(
          "auth/create-operator",
          payload
        );

        // commit("ADD_OPERATOR", response.data.operator);
        await dispatch("fetchOperators");
        return response;
      } catch (error) {
        console.error(
          "Errore durante la creazione di un nuovo operator:",
          error
        );
        throw error;
      }
    },

    // Nuova azione per aggiornare un operatore esistente
    async updateOperator({ commit }, { operatorId, payload }) {
      const response = await axiosInstance.patch(
        `auth/update/${operatorId}`,
        payload
      );
      commit("UPDATE_OPERATOR", response.data.operator);
      return response;
    },

    // Aggiungiamo un’azione per archiviare (o “eliminare”) l’operatore
    async archiveOperator({ commit }, operatorId) {
      try {
        // Faccio la chiamata al backend
        const response = await axiosInstance.post(`auth/archive/${operatorId}`);
        // Se va a buon fine, aggiorno localmente lo store
        commit("REMOVE_OPERATOR", operatorId);
        return response;
      } catch (error) {
        console.error("Errore durante l'archiviazione di un operator:", error);
        throw error;
      }
    },

    // ---------------------------------------------------------------------------//

    async fetchItems(
      { commit },
      { page = 1, limit, category = null, search = "", itemCode = "" } = {}
    ) {
      try {
        const { data } = await axiosInstance.get("/item/get-all", {
          params: {
            page,
            limit,
            ...(category ? { category } : {}),
            ...(search ? { search } : {}),
            ...(itemCode ? { itemCode } : {}),
          },
        });
        commit("SET_ITEMS", Array.isArray(data.results) ? data.results : []);
        commit("SET_PAGINATION", {
          // first: data.first ?? null,
          // next: data.next ?? null,
          // previous: data.previous ?? null,
          // last: data.last ?? null,
          currentPage: data.pagination.currentPage,
          totalPages: data.pagination.totalPages,
          totalItems: data.pagination.totalItems,
          itemsPerPage: data.pagination.itemsPerPage,
        });
        return data.results; // così il componente può usarlo
      } catch (error) {
        if (error.response?.status === 404) {
          // il server dice “nessun articolo: trasformalo in array vuoto
          commit("SET_ITEMS", []);
          // azzera la paginazione per coerenza UI
          commit("SET_PAGINATION", {
            currentPage: 1,
            totalPages: 1,
            totalItems: 0,
            itemsPerPage: 8,
          });
          return [];
        }
        // console.error("fetchClients failed:", error);
        throw error; // lascia passare altri errori (500, 401…)
      }
    },

    // Nuova azione per fetchare gli articoli venduti ai clienti
    async fetchClientSoldItems(_, clientId) {
      try {
        const response = await axiosInstance.get(
          `client/${clientId}/client-info`
        );
        return response.data;
      } catch (error) {
        console.error(
          "Errore durante il caricamento degli articoli venduti associati al cliente",
          error
        );
        throw error;
      }
    },

    // Nuova azione per creare un item
    async createItem({ dispatch }, payload) {
      try {
        const response = await axiosInstance.post("item/create", payload);

        // commit("ADD_ITEM", response.data.item);
        await dispatch("fetchItems");
        return response;
      } catch (error) {
        console.error(
          "Errore durante la creazione di un nuovo articolo:",
          error
        );
        throw error;
      }
    },

    // Nuova azione per aggiornare un item esistente
    async updateItem({ commit }, { itemId, payload }) {
      const response = await axiosInstance.patch(
        `item/update/${itemId}`,
        payload
      );
      commit("UPDATE_ITEM", response.data.item);
      return response;
    },

    // Aggiungiamo un’azione per archiviare (o “eliminare”) l’item
    async archiveItem({ commit }, itemId) {
      try {
        // Faccio la chiamata al backend
        const response = await axiosInstance.post(`item/archive/${itemId}`);
        // Se va a buon fine, aggiorno localmente lo store
        commit("REMOVE_ITEM", itemId);
        return response;
      } catch (error) {
        console.error("Errore durante l'archiviazione dell'articolo:", error);
        throw error;
      }
    },

    // ---------------------------------------------------------------------------//

    async fetchOrders({ commit }, { page = 1, limit = 10, status } = {}) {
      try {
        const { data } = await axiosInstance.get("/order/get-all", {
          params: {
            page,
            limit,
            ...(status ? { status } : {}),
          },
        });
        commit("SET_ORDERS", Array.isArray(data.results) ? data.results : []);
        commit("SET_ORDERS_PAGINATION", data.pagination);
        return data.results;
      } catch (err) {
        if (err.response?.status === 404) {
          commit("SET_ORDERS", []);
          commit("SET_ORDERS_PAGINATION", {
            currentPage: 1,
            totalPages: 1,
            totalItems: 0,
            itemsPerPage: limit,
            startItem: 0,
            endItem: 0,
          });
          return [];
        }
        throw err;
      }
    },

    async createOrder(_, payload) {
      const response = await axiosInstance.post("order/create", payload);
      return response;
    },

    async updateOrder(_, { id, payload }) {
      const response = await axiosInstance.patch(`order/update/${id}`, payload);
      return response;
    },

    async archiveOrder(_, orderId) {
      const response = await axiosInstance.post(`order/archive/${orderId}`);
      return response;
    },

    async splitOrder(_, { id, confirmedQty, suspendedQty }) {
      const response = await axiosInstance.patch(`order/${id}/split`, {
        confirmedQty,
        suspendedQty,
      });
      return response;
    },

    async completeOrder(_, { id, damagedQty = 0 }) {
      const response = await axiosInstance.patch(`order/${id}/complete`, {
        damagedQty,
      });
      return response;
    },

    async fetchActiveOrders({ commit }) {
      const statuses = ["creato", "in_sospeso", "confermato"];
      const results = await Promise.all(
        statuses.map((status) =>
          axiosInstance
            .get("/order/get-all", { params: { status, limit: 10000 } })
            .then((r) => (Array.isArray(r.data.results) ? r.data.results : []))
            .catch((err) =>
              err.response?.status === 404 ? [] : Promise.reject(err)
            )
        )
      );
      const merged = results
        .flat()
        .sort((a, b) => new Date(b.orderedAt) - new Date(a.orderedAt));
      commit("SET_ORDERS", merged);
      commit("SET_ORDERS_PAGINATION", {
        currentPage: 1,
        totalPages: 1,
        totalItems: merged.length,
        itemsPerPage: merged.length,
        startItem: merged.length ? 1 : 0,
        endItem: merged.length,
      });
      return merged;
    },

    // ---------------------------------------------------------------------------//

    async fetchQuotes(
      { commit },
      { page = 1, limit = 7, search = "", archived, isConfirmed } = {}
    ) {
      try {
        const { data } = await axiosInstance.get("/quote/get-all", {
          params: {
            page,
            limit,
            ...(search ? { search } : {}),
            ...(archived !== undefined ? { archived } : {}), // se non è definito, non lo mando
            ...(isConfirmed !== undefined ? { isConfirmed } : {}),
          },
        });
        // console.log("fetchQuotes data", data);
        // console.log("allquotes", data);
        commit("SET_QUOTES", data.results);
        commit("SET_PAGINATION", {
          currentPage: data.pagination.currentPage,
          totalPages: data.pagination.totalPages,
          totalItems: data.pagination.totalItems,
          itemsPerPage: data.pagination.itemsPerPage,
        });
        return data.results; // così il componente può usarlo
      } catch (err) {
        if (err.response?.status === 404) {
          // il server dice “nessun preventivo”: trasformalo in array vuoto
          commit("SET_QUOTES", []);
          throw err;
        }
        console.error("Errore durante il fetch dei preventivi:", err);
        throw err;
      }
    }, // quando verranno usate le rotte del backend

    // Nuova azione per creare un quote
    async createQuote({ dispatch }, payload) {
      try {
        const response = await axiosInstance.post("quote/create", payload);

        // commit("ADD_QUOTE", response.data.quote);
        await dispatch("fetchQuotes");
        return response;
      } catch (error) {
        console.error(
          "Errore durante la creazione di un nuovo preventivo:",
          error
        );
        throw error;
      }
    },

    // Nuova azione per caricare un documento di un quote
    async uploadQuoteDocument({ commit }, { quoteId, document, filename }) {
      try {
        const formData = new FormData();
        formData.append("document", document, filename);

        // POST multipart/form-data alla tua rotta
        // Nota: axiosInstance ha baseURL impostato su '/api'
        const response = await axiosInstance.post(
          `/upload/quotes/${quoteId}/documents`,
          formData,
          { headers: { "Content-Type": "multipart/form-data" } }
        );

        // Il backend restituisce il record creato sotto la chiave "doc"
        // (fallback su "document" per compatibilità). Commit solo se presente,
        // così non inseriamo valori vuoti nello store.
        const uploadedDoc = response.data?.doc || response.data?.document;
        if (uploadedDoc) {
          commit("ADD_DOC_QUOTE", {
            quoteId,
            document: uploadedDoc,
          });
        }
        return response;
      } catch (error) {
        console.error("Errore durante l'upload del documento:", error);
        throw error;
      }
    },

    async sendQuoteDocumentEmail(
      _context,
      { quoteId, objectName, contact = "" }
    ) {
      return await axiosInstance.post(
        `/upload/quotes/${quoteId}/documents/send-email`,
        { objectName, contact }
      );
    },

    // Nuova azione per aggiornare un quote esistente
    async updateQuote({ commit }, { quoteId, payload }) {
      const response = await axiosInstance.patch(
        `quote/update-with-delta/${quoteId}`,
        payload
      );
      commit("UPDATE_QUOTE", response.data.quote);
      return response;
    },

    // Nuova azione per verificare se ci sono preventivi archiviati
    async hasArchivedQuotes() {
      try {
        const { data } = await axiosInstance.get("/quote/get-all", {
          params: { page: 1, limit: 1, archived: true },
        });

        /* nuovo formato: { results: [ … ], pagination: { totalItems, … } } */
        return (data.pagination?.totalItems || 0) > 0;
      } catch (err) {
        console.error(
          "Errore durante il tentantivo di ritrovare i preventivi archiviati: ",
          err
        );
        return false; // fallback sicuro
      }
    },

    // ✅ archivia preventivo
    async archiveQuote({ commit }, quoteId) {
      try {
        const { data } = await axiosInstance.post(`quote/archive/${quoteId}`, {
          archived: true,
        });
        // rimuovo dalla lista corrente (in "attivi" non deve più vedersi)
        commit("REMOVE_QUOTE", quoteId);
        return { data }; // data = { message, quote }
      } catch (error) {
        console.error("Errore durante l'archiviazione del preventivo:", error);
        throw error;
      }
    },

    // ✅ ripristina preventivo (nuova azione)
    async restoreQuote({ commit }, quoteId) {
      try {
        const { data } = await axiosInstance.post(`quote/archive/${quoteId}`, {
          archived: false,
        });
        // rimuovo dalla lista corrente (in "archiviati" non deve più vedersi)
        commit("REMOVE_QUOTE", quoteId);
        return { data }; // data = { message, quote }
      } catch (error) {
        console.error("Errore durante il ripristino del preventivo:", error);
        throw error;
      }
    },
  },
});

export default store;
